import { css } from 'styled-components';
import { DIMENSIONS } from '../defaultConfig.js';
import { getInternalConfig, getConfig, mediaInternal } from './lib.js';

const calcPadding = ({
  $padding,
  $paddingLeft,
  $paddingRight
}) => {
  if ($padding) {
    $paddingLeft = typeof $padding === 'object' ? $padding : {
      xs: $padding
    };
    $paddingRight = typeof $padding === 'object' ? $padding : {
      xs: $padding
    };
  } else {
    $paddingLeft = typeof $paddingLeft === 'object' ? $paddingLeft : {
      xs: $paddingLeft
    };
    $paddingRight = typeof $paddingRight === 'object' ? $paddingRight : {
      xs: $paddingRight
    };
  }
  let lastPaddingLeft;
  let lastPaddingRight;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const paddingLeft = $paddingLeft[screenSize];
    const paddingRight = $paddingRight[screenSize];
    if (paddingLeft !== undefined) {
      lastPaddingLeft = paddingLeft;
    }
    if (paddingRight !== undefined) {
      lastPaddingRight = paddingRight;
    }
    if (paddingLeft !== undefined || paddingRight !== undefined) {
      return `
                padding-left: ${paddingLeft ?? lastPaddingLeft};
                padding-right: ${paddingRight ?? lastPaddingRight};
            `;
    }
    return null;
  });
  return mediaQuery;
};
const calcGap = ({
  $hasNoGap = false,
  theme
}) => {
  if (typeof $hasNoGap === 'boolean' || typeof $hasNoGap === 'string') {
    $hasNoGap = {
      xs: $hasNoGap
    };
  }
  let lastGap;
  let lastGapConfig;
  let lastColumnGapVar;
  let lastRowGap;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const gap = getInternalConfig(theme).columnGap[screenSize];
    const gapConfig = $hasNoGap[screenSize];
    if (gap !== undefined) {
      lastGap = gap;
    }
    if (gapConfig !== undefined) {
      lastGapConfig = gapConfig;
    }
    const columnGapVar = `--column-gap: ${(gapConfig ?? lastGapConfig) === 'no-column' || (gapConfig ?? lastGapConfig) === true ? '0px' : `${String(gap ?? lastGap)}px`};`;
    const columnGap = 'column-gap: var(--column-gap);';
    const rowGap = `row-gap: ${(gapConfig ?? lastGapConfig) === 'no-row' || (gapConfig ?? lastGapConfig) === true ? '0px' : `${String(gap ?? lastGap)}px`};`;
    if (lastColumnGapVar !== columnGapVar || lastRowGap !== rowGap) {
      lastColumnGapVar = columnGapVar;
      lastRowGap = rowGap;
      return `
                ${columnGapVar}
                ${columnGap}
                ${rowGap}
            `;
    }
    return null;
  });
  return mediaQuery;
};
const calcSizes = ({
  $sizes,
  theme
}) => {
  let lastColCount;
  let lastColSize = 'auto';
  let lastRealSize;
  if (Object.keys($sizes).length > 0) {
    const mediaQuery = DIMENSIONS.map(screenSize => {
      const colCount = getInternalConfig(theme).columns[screenSize];
      const size = $sizes[screenSize];
      let realSize;
      if (colCount) {
        lastColCount = colCount;
      }
      if (size) {
        lastColSize = size;
      }
      if (['auto', 'max-content', 'min-content'].includes(size ?? lastColSize)) {
        realSize = size ?? lastColSize;
      } else {
        const col = colCount ?? lastColCount;
        const cappedSize = (size ?? lastColSize) > col ? col : size ?? lastColSize;
        realSize = `calc(100% / ${col} * ${cappedSize} - var(--column-gap, 0px) + var(--column-gap, 0px) * ${cappedSize} / ${col})`;
      }
      if (lastRealSize !== realSize) {
        lastRealSize = realSize;
        return `
                    flex: ${realSize === 'auto' ? 'auto' : `0 0 ${realSize}`};
                    max-width: ${realSize === 'auto' ? 'initial' : realSize};
                `;
      }
      return null;
    });
    return mediaQuery;
  }
  return null;
};
const calcOffset = ({
  $offset,
  theme
}) => {
  if (typeof $offset !== 'object') {
    $offset = {
      xs: $offset
    };
  }
  let lastColCount;
  let lastOffset = 0;
  let lastRealOffset;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const colCount = getInternalConfig(theme).columns[screenSize];
    const currentOffset = $offset[screenSize];
    if (colCount) {
      lastColCount = colCount;
    }
    if (currentOffset) {
      lastOffset = currentOffset;
    }
    const col = colCount ?? lastColCount;
    const finalOffset = currentOffset ?? lastOffset;
    const realOffset = finalOffset === 0 ? null : `calc(100% / ${col} * ${finalOffset} + var(--column-gap, 0px) * ${finalOffset} / ${col})`;
    if (lastRealOffset !== realOffset) {
      lastRealOffset = realOffset;
      return `margin-left: ${realOffset};`;
    }
    return null;
  });
  return mediaQuery;
};
const calcDirection = defaultDirection => ({
  $direction,
  $hasNoWrap,
  $isReverse
}) => {
  if (typeof $direction === 'string') {
    $direction = {
      xs: $direction
    };
  } else if (typeof $direction === 'undefined') {
    $direction = {
      xs: defaultDirection
    };
  } else if (typeof $direction.xs === 'undefined') {
    $direction = {
      ...$direction,
      xs: defaultDirection
    };
  }
  if (!Array.isArray($hasNoWrap)) {
    $hasNoWrap = $hasNoWrap ? DIMENSIONS : [];
  }
  if (!Array.isArray($isReverse)) {
    $isReverse = $isReverse ? DIMENSIONS : [];
  }
  let lastDirection;
  let lastDirectionString;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const currentDirection = $direction[screenSize];
    const currentWrap = $hasNoWrap.includes(screenSize) || (currentDirection ?? lastDirection) === 'column' ? 'nowrap' : 'wrap';
    const currentReverse = $isReverse.includes(screenSize) ? '-reverse' : '';
    const directionString = `${currentDirection ?? lastDirection}${currentReverse} ${currentWrap}${currentWrap === 'nowrap' ? '' : currentReverse}`;
    if (currentDirection) {
      lastDirection = currentDirection;
    }
    if (lastDirectionString !== directionString) {
      lastDirectionString = directionString;
      return `flex-flow: ${directionString};`;
    }
    return null;
  });
  return mediaQuery;
};
const cssKeys = {
  $align: 'align-items',
  $justify: 'justify-content',
  $order: 'order'
};
const calcAlignmentProps = prop => ({
  [prop]: cssProp
}) => {
  if (typeof cssProp !== 'object') {
    cssProp = {
      xs: cssProp
    };
  }
  const cssKey = cssKeys[prop];
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const currentCssProp = cssProp[screenSize];
    if (currentCssProp) {
      return `${cssKey}: ${currentCssProp};`;
    }
    return null;
  });
  return mediaQuery;
};
const calcContainerPadding = ({
  $hasNoPadding,
  theme
}) => {
  if (!Array.isArray($hasNoPadding)) {
    $hasNoPadding = $hasNoPadding ? DIMENSIONS : [];
  }
  let lastRealPadding = 0;
  let lastPadding;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const containerPadding = getInternalConfig(theme).containerPadding[screenSize];
    const hasNoPadding = $hasNoPadding.includes(screenSize);
    const realPadding = containerPadding ?? lastRealPadding;
    const padding = hasNoPadding ? 0 : realPadding;
    if (padding !== lastPadding || realPadding !== lastRealPadding) {
      lastRealPadding = realPadding;
      lastPadding = padding;
      return `
                padding-left: ${String(padding)}px;
                padding-right: ${String(padding)}px;
            `;
    }
    return null;
  });
  return mediaQuery;
};
const calcContainerSize = ({
  $isFluid,
  $maxWidth,
  theme
}) => {
  if (!Array.isArray($isFluid)) {
    $isFluid = $isFluid ? DIMENSIONS : [];
  }
  let lastWidth;
  let lastContainerSize;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const actualFluid = $isFluid.includes(screenSize);
    const containerSize = getInternalConfig(theme).container[screenSize];
    const width = actualFluid || (containerSize ?? lastContainerSize) === 'fluid' ? '100%' : `${$maxWidth ?? containerSize ?? lastContainerSize}px`;
    if (containerSize) {
      lastContainerSize = containerSize;
    }
    if (width !== lastWidth) {
      lastWidth = width;
      return `width: ${String(width)};`;
    }
    return null;
  });
  return mediaQuery;
};
const calcSpacerInline = ({
  $isInline
}) => {
  if (!Array.isArray($isInline)) {
    $isInline = $isInline ? DIMENSIONS : [];
  }
  let lastInline;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const inline = $isInline.includes(screenSize) ? 'inline-block' : 'block';
    if (inline !== lastInline) {
      lastInline = inline;
      return `display: ${inline};`;
    }
    return null;
  });
  return mediaQuery;
};
const spacerCssKeys = {
  height: '$y',
  'min-height': '$y',
  'min-width': '$x',
  width: '$x'
};
const calcSpacerMeasures = propKey => ({
  theme,
  [spacerCssKeys[propKey]]: value
}) => {
  if (typeof value !== 'object') {
    value = {
      xs: value
    };
  }
  const spacing = getInternalConfig(theme).baseSpacing;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const currentValue = value[screenSize];
    if (currentValue !== undefined) {
      return `${propKey}: ${String(currentValue * spacing)}rem;`;
    }
    return null;
  });
  return mediaQuery;
};
const spacerMaxCssKeys = {
  'max-height': ['$maxY', '$y'],
  'max-width': ['$maxX', '$x']
};
const calcSpacerMaxValues = propKey => componentProps => {
  const {
    theme
  } = componentProps;
  let {
    $isNotStretching
  } = componentProps;
  let max = componentProps[spacerMaxCssKeys[propKey][0]];
  let auto = componentProps[spacerMaxCssKeys[propKey][1]];
  if (typeof auto !== 'object') {
    auto = {
      xs: auto
    };
  }
  if (typeof max !== 'object') {
    max = {
      xs: max
    };
  }
  if (!Array.isArray($isNotStretching)) {
    $isNotStretching = $isNotStretching ? DIMENSIONS : [];
  }
  const spacing = getInternalConfig(theme).baseSpacing;
  let lastAuto;
  let lastMax;
  let lastRealCss;
  const mediaQuery = DIMENSIONS.map(screenSize => {
    const currentAuto = auto[screenSize];
    const currentMax = max[screenSize];
    const currentNoStretch = $isNotStretching.includes(screenSize);
    if (currentAuto !== undefined) {
      lastAuto = currentAuto;
    }
    if (currentMax !== undefined) {
      lastMax = currentMax;
    }
    const realMax = currentMax ?? lastMax;
    const realAuto = currentAuto ?? lastAuto;
    const realCss = realMax !== undefined ? `${realMax * spacing}rem` : currentNoStretch ? realAuto !== undefined ? `${realAuto * spacing}rem` : null : lastRealCss ? 'initial' : null;
    if (realCss !== lastRealCss && realCss !== null) {
      lastRealCss = realCss;
      return `${propKey}: ${String(realCss)};`;
    }
    return null;
  });
  return mediaQuery;
};
const calcSkeletonDirection = ({
  $variant,
  theme
}) => {
  const skeletonConfig = getInternalConfig(theme).skeleton[$variant];
  return skeletonConfig.animation.direction === 'rtl' ? 'reverse' : 'normal';
};
const calcSkeletonDuration = ({
  $variant,
  theme
}) => {
  const skeletonConfig = getInternalConfig(theme).skeleton[$variant];
  return `${skeletonConfig.animation.duration}s`;
};
const calcSkeletonBorderRadius = ({
  $circle,
  $variant,
  theme
}) => {
  const skeletonConfig = getInternalConfig(theme).skeleton[$variant];
  return $circle ? '50%' : `${skeletonConfig.borderRadius}rem`;
};
const calcSkeletonBackgroundColor = ({
  $variant,
  theme
}) => {
  const skeletonConfig = getInternalConfig(theme).skeleton[$variant];
  return skeletonConfig.colors.base;
};
const calcSkeletonBackgroundImage = ({
  $variant,
  theme
}) => {
  const skeletonConfig = getInternalConfig(theme).skeleton[$variant];
  return `linear-gradient(90deg, ${skeletonConfig.colors.baseHighlight} 8%, ${skeletonConfig.colors.highlight} 38%, ${skeletonConfig.colors.baseHighlight} 54%);`;
};
const calcSkeletonWidth = ({
  $width
}) => {
  if ($width === undefined) {
    return '100%';
  }
  if (typeof $width === 'string') {
    return $width;
  }
  return `${$width}px`;
};
const calcSkeletonHeight = ({
  $height
}) => {
  if ($height === undefined) {
    return null;
  }
  if (typeof $height === 'string') {
    return $height;
  }
  return `${$height}px`;
};
const debugCss = element => ({
  theme
}) => {
  if (process.env.NODE_ENV !== 'production') {
    const backgroundColor = getConfig(theme).debug[element].background;
    const paddingColor = getConfig(theme).debug[element].padding;
    const outlineColor = getConfig(theme).debug[element].outline;
    return css`
            &.debug {
                background-clip: content-box, padding-box;
                background-image:
                    linear-gradient(to bottom, ${backgroundColor} 0%, ${backgroundColor} 100%),
                    linear-gradient(to bottom, ${paddingColor} 0%, ${paddingColor} 100%);
                outline: ${outlineColor} solid 1px;
            }
        `;
  }
  return null;
};
const mergeMediaQueries = (...cssFunctions) => props => {
  const mediaQueries = cssFunctions.map(cssFunction => cssFunction(props));
  return DIMENSIONS.map((screenSize, index) => {
    const mediaQuery = mediaQueries.map(query => query?.[index]).filter(Boolean).join('');
    return mediaQuery ? mediaInternal(screenSize, props.theme)`${mediaQuery}` : null;
  });
};

export { calcAlignmentProps, calcContainerPadding, calcContainerSize, calcDirection, calcGap, calcOffset, calcPadding, calcSizes, calcSkeletonBackgroundColor, calcSkeletonBackgroundImage, calcSkeletonBorderRadius, calcSkeletonDirection, calcSkeletonDuration, calcSkeletonHeight, calcSkeletonWidth, calcSpacerInline, calcSpacerMaxValues, calcSpacerMeasures, debugCss, mergeMediaQueries };
//# sourceMappingURL=styleHelpers.js.map
